import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useDashboardPreferences } from '../../context/DashboardPreferencesContext';
import { getCardById } from '../../config/dashboardCardRegistry';
import { X, Eye, EyeOff, Plus, Lock, GripVertical, RotateCcw } from 'lucide-react';
import './DashboardConfigurator.css';

function DashboardConfigurator({ isOpen, onClose }) {
  const {
    getAllCards,
    getVisibleCards,
    toggleCardVisibility,
    reorderCards,
    resetDashboard,
  } = useDashboardPreferences();

  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const dragCounter = useRef(0);
  const panelRef = useRef(null);

  const allCards = getAllCards();
  const visibleCards = getVisibleCards();
  const hiddenCards = allCards.filter(c => !c.visible);

  // Close on Escape
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Focus trap
  useEffect(() => {
    if (isOpen && panelRef.current) {
      panelRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = useCallback((cardId) => {
    toggleCardVisibility(cardId);
  }, [toggleCardVisibility]);

  const handleReset = useCallback(async () => {
    await resetDashboard();
    setShowResetConfirm(false);
  }, [resetDashboard]);

  // Drag and drop for visible cards reordering
  const handleDragStart = useCallback((e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    requestAnimationFrame(() => {
      e.target.closest('.configurator-card-item')?.classList.add('configurator-item-dragging');
    });
  }, []);

  const handleDragEnd = useCallback((e) => {
    e.target.closest('.configurator-card-item')?.classList.remove('configurator-item-dragging');
    setDragIndex(null);
    setDragOverIndex(null);
    dragCounter.current = 0;
  }, []);

  const handleDragEnter = useCallback((e, index) => {
    e.preventDefault();
    dragCounter.current++;
    setDragOverIndex(index);
  }, []);

  const handleDragLeave = useCallback(() => {
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setDragOverIndex(null);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback((e, dropIndex) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragOverIndex(null);

    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null);
      return;
    }

    const reordered = [...visibleCards];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, moved);

    // Merge with hidden cards
    const full = [...reordered, ...hiddenCards];
    reorderCards(full);
    setDragIndex(null);
  }, [dragIndex, visibleCards, hiddenCards, reorderCards]);

  // Keyboard reordering
  const handleKeyDown = useCallback((e, index) => {
    if (e.key === 'ArrowUp' && index > 0) {
      e.preventDefault();
      const reordered = [...visibleCards];
      [reordered[index - 1], reordered[index]] = [reordered[index], reordered[index - 1]];
      const full = [...reordered, ...hiddenCards];
      reorderCards(full);
      requestAnimationFrame(() => {
        const handles = document.querySelectorAll('.configurator-drag-handle');
        if (handles[index - 1]) handles[index - 1].focus();
      });
    } else if (e.key === 'ArrowDown' && index < visibleCards.length - 1) {
      e.preventDefault();
      const reordered = [...visibleCards];
      [reordered[index], reordered[index + 1]] = [reordered[index + 1], reordered[index]];
      const full = [...reordered, ...hiddenCards];
      reorderCards(full);
      requestAnimationFrame(() => {
        const handles = document.querySelectorAll('.configurator-drag-handle');
        if (handles[index + 1]) handles[index + 1].focus();
      });
    }
  }, [visibleCards, hiddenCards, reorderCards]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`configurator-backdrop ${isOpen ? 'open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className={`configurator-panel ${isOpen ? 'open' : ''}`}
        role="dialog"
        aria-label="Customize Dashboard"
        tabIndex={-1}
      >
        {/* Header */}
        <div className="configurator-panel-header">
          <h2 className="configurator-panel-title">Customize Dashboard</h2>
          <button className="btn btn-icon" onClick={onClose} aria-label="Close panel">
            <X size={20} />
          </button>
        </div>

        {/* Actions */}
        <div className="configurator-panel-actions">
          <span className="configurator-count">
            {visibleCards.length} of {allCards.length} cards visible
          </span>
          {!showResetConfirm ? (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setShowResetConfirm(true)}
            >
              <RotateCcw size={14} />
              Reset to Defaults
            </button>
          ) : (
            <div className="configurator-reset-confirm">
              <span>Are you sure?</span>
              <button className="btn btn-danger btn-sm" onClick={handleReset}>
                Yes, Reset
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowResetConfirm(false)}>
                Cancel
              </button>
            </div>
          )}
        </div>

        {/* Visible Cards */}
        <div className="configurator-section">
          <div className="configurator-section-header">
            <Eye size={16} />
            <span>Visible ({visibleCards.length})</span>
          </div>
          <div className="configurator-card-list">
            {visibleCards.map((card, index) => {
              const registry = getCardById(card.id);
              const isLocked = registry?.locked;

              return (
                <div
                  key={card.id}
                  className={`configurator-card-item ${dragOverIndex === index ? 'configurator-item-drag-over' : ''}`}
                  draggable={!isLocked}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  onDragEnter={(e) => handleDragEnter(e, index)}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index)}
                >
                  {!isLocked && (
                    <span
                      className="configurator-drag-handle"
                      tabIndex={0}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      aria-label={`Reorder ${registry?.label || card.id}. Use arrow keys.`}
                    >
                      <GripVertical size={14} />
                    </span>
                  )}
                  <span className="configurator-card-label">
                    {registry?.label || card.id}
                    <span className="configurator-card-size">{card.size}</span>
                  </span>
                  {isLocked ? (
                    <span className="configurator-lock-icon" title="This card cannot be hidden">
                      <Lock size={14} />
                    </span>
                  ) : (
                    <button
                      className="btn btn-icon configurator-toggle-btn"
                      onClick={() => handleToggle(card.id)}
                      title="Hide card"
                      aria-label={`Hide ${registry?.label || card.id}`}
                    >
                      <EyeOff size={16} />
                    </button>
                  )}
                </div>
              );
            })}
            {visibleCards.length === 0 && (
              <div className="configurator-empty">No visible cards</div>
            )}
          </div>
        </div>

        {/* Hidden Cards */}
        {hiddenCards.length > 0 && (
          <div className="configurator-section">
            <div className="configurator-section-header">
              <EyeOff size={16} />
              <span>Hidden ({hiddenCards.length})</span>
            </div>
            <div className="configurator-card-list">
              {hiddenCards.map(card => {
                const registry = getCardById(card.id);
                return (
                  <div key={card.id} className="configurator-card-item configurator-card-hidden">
                    <span className="configurator-card-label">
                      {registry?.label || card.id}
                      <span className="configurator-card-size">{card.size}</span>
                    </span>
                    <button
                      className="btn btn-icon configurator-toggle-btn"
                      onClick={() => handleToggle(card.id)}
                      title="Show card"
                      aria-label={`Show ${registry?.label || card.id}`}
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default DashboardConfigurator;
